/**
 * Shopify Server-Side Tracking Pixel
 * Complete client-side tracking implementation for server-side event forwarding
 * 
 * @version 1.0.0
 * @description Captures 10 mandatory parameters and tracks Shopify Web Pixel API events
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION
  // ============================================================================
  
  console.log('[TRACKING] Script loading started');
  console.log('[TRACKING] Reading configuration from window.__TRACKING_CONFIG__');
  
  var rawServerUrl = (window.__TRACKING_CONFIG__?.serverUrl || '').trim();
  var serverUrlNormalized = rawServerUrl ? rawServerUrl.replace(/\/+$/, '') : '';
  // Fallback for local dev when theme has not set Server URL (empty or placeholder)
  if (!serverUrlNormalized || serverUrlNormalized === 'https://your-server.com') {
    serverUrlNormalized = 'https://c39d-2401-4900-8fec-81a8-1546-6a4d-3a01-af87.ngrok-free.app'; // TEMP: ngrok dev URL — replace with production URL before go-live
    if (!rawServerUrl) {
      console.log('[TRACKING] Server URL not set in theme; using fallback http://localhost:3000. Set in Theme Customize → Server-Side Tracking to override.');
    }
  }

  const CONFIG = {
    // Settings from extension configuration (will be injected by Liquid)
    enableTracking: window.__TRACKING_CONFIG__?.enableTracking !== false,
    enableLogs: window.__TRACKING_CONFIG__?.enableLogs === true,
    apiEndpoint: window.__TRACKING_CONFIG__?.apiEndpoint || '/api/track',
    serverUrl: serverUrlNormalized,
    
    // Cookie settings
    cookieExpiry: 180, // days
    cookieSameSite: 'Lax',
    
    // Session storage keys
    SESSION_ID_KEY: 'tracking_session_id',
    LAST_CLICK_SOURCE_KEY: 'tracking_last_click_source',
    LAST_CLICK_CAMPAIGN_KEY: 'tracking_last_click_campaign',
    LANDING_PAGE_KEY: 'tracking_landing_page',
    
    // Cookie keys
    USER_ID_KEY: 'tracking_user_id',
    FIRST_CLICK_SOURCE_KEY: 'tracking_first_click_source',
    FIRST_CLICK_CAMPAIGN_KEY: 'tracking_first_click_campaign',
    FBP_KEY: '_fbp',
    FBC_KEY: '_fbc',
    
    // Retry settings
    maxRetries: 3,
    retryDelay: 1000, // ms
  };
  
  console.log('[TRACKING] Configuration loaded:', {
    enableTracking: CONFIG.enableTracking,
    enableLogs: CONFIG.enableLogs,
    apiEndpoint: CONFIG.apiEndpoint,
    serverUrl: CONFIG.serverUrl || 'NOT SET',
    cookieExpiry: CONFIG.cookieExpiry,
    cookieSameSite: CONFIG.cookieSameSite
  });

  // ============================================================================
  // LOGGER UTILITY
  // ============================================================================
  
  const Logger = {
    log: function(...args) {
      console.log('[TRACKING]', ...args);
    },
    error: function(...args) {
      console.error('[TRACKING ERROR]', ...args);
    },
    warn: function(...args) {
      console.warn('[TRACKING WARN]', ...args);
    },
    debug: function(...args) {
      if (CONFIG.enableLogs) {
        console.log('[TRACKING DEBUG]', ...args);
      }
    }
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Get cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null
   */
  function getCookie(name) {
    console.log('[TRACKING] getCookie called for:', name);
    try {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        const cookieValue = parts.pop().split(';').shift();
        console.log('[TRACKING] Cookie found:', name, '=', cookieValue);
        return cookieValue;
      }
      console.log('[TRACKING] Cookie not found:', name);
    } catch (e) {
      Logger.error('Error reading cookie:', name, e);
    }
    return null;
  }

  /**
   * Set cookie with expiration and SameSite attribute
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Expiration in days
   */
  function setCookie(name, value, days) {
    console.log('[TRACKING] setCookie called:', name, '=', value, 'expires in', days, 'days');
    try {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      const expires = `expires=${date.toUTCString()}`;
      document.cookie = `${name}=${value};${expires};path=/;SameSite=${CONFIG.cookieSameSite}`;
      console.log('[TRACKING] Cookie set successfully:', name);
      Logger.debug('Cookie details:', { name, value, expires: date.toUTCString(), days });
    } catch (e) {
      Logger.error('Error setting cookie:', name, e);
    }
  }

  /**
   * Generate unique ID with prefix
   * @param {string} prefix - ID prefix
   * @returns {string} Unique ID
   */
  function generateId(prefix) {
    console.log('[TRACKING] generateId called with prefix:', prefix);
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const id = `${prefix}_${timestamp}_${random}`;
    console.log('[TRACKING] Generated ID:', id);
    return id;
  }

  /**
   * Get URL parameter value
   * @param {string} name - Parameter name
   * @returns {string|null} Parameter value or null
   */
  function getUrlParameter(name) {
    console.log('[TRACKING] getUrlParameter called for:', name);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const value = urlParams.get(name);
      if (value) {
        console.log('[TRACKING] URL parameter found:', name, '=', value);
      } else {
        console.log('[TRACKING] URL parameter not found:', name);
      }
      return value;
    } catch (e) {
      Logger.error('Error reading URL parameter:', name, e);
      return null;
    }
  }

  /**
   * Normalize URL for comparison (remove protocol, www, trailing slashes)
   * @param {string} url - URL to normalize
   * @returns {string} Normalized URL
   */
  function normalizeUrl(url) {
    console.log('[TRACKING] normalizeUrl called for:', url);
    try {
      if (!url) {
        console.log('[TRACKING] Empty URL, returning empty string');
        return '';
      }
      const urlObj = new URL(url);
      let normalized = urlObj.hostname.replace(/^www\./, '') + urlObj.pathname;
      normalized = normalized.replace(/\/$/, '');
      const result = normalized.toLowerCase();
      console.log('[TRACKING] URL normalized:', url, '->', result);
      return result;
    } catch (e) {
      Logger.error('Error normalizing URL:', url, e);
      return url || '';
    }
  }

  /**
   * Intelligent source detection from referrer or UTM
   * @returns {string} Source identifier
   */
  function getSource() {
    console.log('[TRACKING] getSource called');
    // Check UTM source first
    const utmSource = getUrlParameter('utm_source');
    if (utmSource) {
      const source = utmSource.toLowerCase();
      console.log('[TRACKING] Source detected from UTM:', source);
      return source;
    }

    // Check referrer
    try {
      const referrer = document.referrer;
      console.log('[TRACKING] Document referrer:', referrer);
      if (!referrer) {
        console.log('[TRACKING] No referrer, source: direct');
        return 'direct';
      }

      const referrerUrl = normalizeUrl(referrer);
      console.log('[TRACKING] Normalized referrer URL:', referrerUrl);

      // Known sources
      if (referrerUrl.includes('google.com') || referrerUrl.includes('google.')) {
        console.log('[TRACKING] Source detected: google');
        return 'google';
      }
      if (referrerUrl.includes('facebook.com') || referrerUrl.includes('fb.com')) {
        console.log('[TRACKING] Source detected: facebook');
        return 'facebook';
      }
      if (referrerUrl.includes('instagram.com')) {
        console.log('[TRACKING] Source detected: instagram');
        return 'instagram';
      }
      if (referrerUrl.includes('youtube.com') || referrerUrl.includes('youtu.be')) {
        console.log('[TRACKING] Source detected: youtube');
        return 'youtube';
      }
      if (referrerUrl.includes('twitter.com') || referrerUrl.includes('x.com')) {
        console.log('[TRACKING] Source detected: twitter');
        return 'twitter';
      }

      // Extract domain from referrer
      try {
        const url = new URL(referrer);
        const domain = url.hostname.replace(/^www\./, '');
        console.log('[TRACKING] Source detected from domain:', domain);
        return domain;
      } catch (e) {
        console.log('[TRACKING] Error extracting domain, returning: unknown');
        return 'unknown';
      }
    } catch (e) {
      Logger.error('Error detecting source:', e);
      return 'direct';
    }
  }

  /**
   * Get campaign from UTM parameter
   * @returns {string|null} Campaign value or null
   */
  function getCampaign() {
    console.log('[TRACKING] getCampaign called');
    const campaign = getUrlParameter('utm_campaign');
    console.log('[TRACKING] Campaign value:', campaign || 'null');
    return campaign;
  }

  /**
   * Initialize first-click attribution (only once per user)
   */
  function initFirstClick() {
    console.log('[TRACKING] initFirstClick called');
    // Check if first-click already set
    const existingSource = getCookie(CONFIG.FIRST_CLICK_SOURCE_KEY);
    if (existingSource) {
      console.log('[TRACKING] First-click attribution already set:', existingSource);
      return;
    }

    console.log('[TRACKING] First-click attribution not set, initializing...');
    const source = getSource();
    const campaign = getCampaign();

    if (source && source !== 'direct') {
      setCookie(CONFIG.FIRST_CLICK_SOURCE_KEY, source, CONFIG.cookieExpiry);
      console.log('[TRACKING] First-click source set:', source);
    } else {
      console.log('[TRACKING] Source is direct or empty, not setting first-click source');
    }

    if (campaign) {
      setCookie(CONFIG.FIRST_CLICK_CAMPAIGN_KEY, campaign, CONFIG.cookieExpiry);
      console.log('[TRACKING] First-click campaign set:', campaign);
    } else {
      console.log('[TRACKING] No campaign found, not setting first-click campaign');
    }
  }

  /**
   * Update last-click attribution (session-based)
   */
  function updateLastClick() {
    console.log('[TRACKING] updateLastClick called');
    const source = getSource();
    const campaign = getCampaign();

    if (source && source !== 'direct') {
      try {
        sessionStorage.setItem(CONFIG.LAST_CLICK_SOURCE_KEY, source);
        console.log('[TRACKING] Last-click source updated in sessionStorage:', source);
      } catch (e) {
        Logger.error('Error setting sessionStorage:', e);
      }
    } else {
      console.log('[TRACKING] Source is direct or empty, not updating last-click source');
    }

    if (campaign) {
      try {
        sessionStorage.setItem(CONFIG.LAST_CLICK_CAMPAIGN_KEY, campaign);
        console.log('[TRACKING] Last-click campaign updated in sessionStorage:', campaign);
      } catch (e) {
        Logger.error('Error setting sessionStorage:', e);
      }
    } else {
      console.log('[TRACKING] No campaign found, not updating last-click campaign');
    }
  }

  /**
   * Get or create session ID
   * @returns {string} Session ID
   */
  function getSessionId() {
    console.log('[TRACKING] getSessionId called');
    try {
      let sessionId = sessionStorage.getItem(CONFIG.SESSION_ID_KEY);
      if (!sessionId) {
        console.log('[TRACKING] No existing session ID, creating new one');
        sessionId = generateId('session');
        sessionStorage.setItem(CONFIG.SESSION_ID_KEY, sessionId);
        console.log('[TRACKING] New session ID created and stored:', sessionId);
      } else {
        console.log('[TRACKING] Existing session ID found:', sessionId);
      }
      return sessionId;
    } catch (e) {
      Logger.error('Error with sessionStorage:', e);
      const fallbackId = generateId('session');
      console.log('[TRACKING] Using fallback session ID:', fallbackId);
      return fallbackId;
    }
  }

  /**
   * Get or create user ID (cookie-based for cross-tab support)
   * @returns {string} User ID
   */
  function getUserId() {
    console.log('[TRACKING] getUserId called');
    let userId = getCookie(CONFIG.USER_ID_KEY);
    if (!userId) {
      console.log('[TRACKING] No existing user ID, creating new one');
      userId = generateId('user');
      setCookie(CONFIG.USER_ID_KEY, userId, CONFIG.cookieExpiry);
      console.log('[TRACKING] New user ID created and stored:', userId);
    } else {
      console.log('[TRACKING] Existing user ID found:', userId);
    }
    return userId;
  }

  /**
   * Get Facebook Browser ID from _fbp cookie
   * @returns {string|null} FBP value or null
   */
  function getFbp() {
    console.log('[TRACKING] getFbp called');
    const fbp = getCookie(CONFIG.FBP_KEY);
    console.log('[TRACKING] FBP value:', fbp || 'null');
    return fbp;
  }

  /**
   * Get Facebook Click ID from _fbc cookie or fbclid URL param
   * @returns {string|null} FBC value or null
   */
  function getFbc() {
    console.log('[TRACKING] getFbc called');
    // Check cookie first
    let fbc = getCookie(CONFIG.FBC_KEY);
    if (fbc) {
      console.log('[TRACKING] FBC found in cookie:', fbc);
      return fbc;
    }

    console.log('[TRACKING] FBC not in cookie, checking URL parameter fbclid');
    // Check URL parameter
    const fbclid = getUrlParameter('fbclid');
    if (fbclid) {
      // Format: fb.{subdomain_index}.{click_id}.{timestamp}
      const timestamp = Math.floor(Date.now() / 1000);
      fbc = `fb.1.${timestamp}.${fbclid}`;
      setCookie(CONFIG.FBC_KEY, fbc, CONFIG.cookieExpiry);
      console.log('[TRACKING] FBC created from fbclid URL parameter:', fbc);
      return fbc;
    }

    console.log('[TRACKING] No FBC found in cookie or URL parameter');
    return null;
  }

  /**
   * Get landing page URL (first page in session)
   * @returns {string} Landing page URL
   */
  function getLandingPage() {
    console.log('[TRACKING] getLandingPage called');
    try {
      let landingPage = sessionStorage.getItem(CONFIG.LANDING_PAGE_KEY);
      if (!landingPage) {
        landingPage = window.location.href;
        sessionStorage.setItem(CONFIG.LANDING_PAGE_KEY, landingPage);
        console.log('[TRACKING] Landing page set in sessionStorage:', landingPage);
      } else {
        console.log('[TRACKING] Existing landing page found:', landingPage);
      }
      return landingPage;
    } catch (e) {
      Logger.error('Error with sessionStorage:', e);
      const currentUrl = window.location.href;
      console.log('[TRACKING] Using current URL as fallback landing page:', currentUrl);
      return currentUrl;
    }
  }

  /**
   * Get all 10 mandatory tracking parameters
   * @returns {Object} Tracking parameters object
   */
  function getTrackingParams() {
    console.log('[TRACKING] getTrackingParams called - collecting all 10 parameters');
    const params = {
      session_id: getSessionId(),
      user_id: getUserId(),
      fbp: getFbp(),
      fbc: getFbc(),
      first_click_source: getCookie(CONFIG.FIRST_CLICK_SOURCE_KEY),
      last_click_source: (() => {
        try {
          return sessionStorage.getItem(CONFIG.LAST_CLICK_SOURCE_KEY);
        } catch (e) {
          return null;
        }
      })(),
      first_click_campaign: getCookie(CONFIG.FIRST_CLICK_CAMPAIGN_KEY),
      last_click_campaign: (() => {
        try {
          return sessionStorage.getItem(CONFIG.LAST_CLICK_CAMPAIGN_KEY);
        } catch (e) {
          return null;
        }
      })(),
      landing_page: getLandingPage(),
      user_agent: navigator.userAgent
    };
    console.log('[TRACKING] All 10 tracking parameters collected:', params);
    return params;
  }

  // ============================================================================
  // API COMMUNICATION — POST {BASE_URL}/api/track (Content-Type: application/json)
  // ============================================================================

  /**
   * Send event to backend API with retry logic.
   * POST to CONFIG.serverUrl + CONFIG.apiEndpoint (e.g. https://api.example.com/api/track).
   * Body: JSON with required event_name and optional timestamp, session_id, user_id, fbp, fbc, attribution, landing_page, user_agent, product_id, order_id, email, value, etc.
   * @param {Object} eventData - Event payload (must include event_name)
   * @param {number} retryCount - Current retry attempt
   */
  function sendEvent(eventData, retryCount = 0) {
    console.log('[TRACKING] sendEvent called', { eventName: eventData.event_name, retryCount });
    
    if (!CONFIG.enableTracking) {
      console.log('[TRACKING] Tracking disabled, skipping event');
      return;
    }

    var endpointPath = (CONFIG.apiEndpoint || '/api/track').replace(/^\//, '');
    var base = (CONFIG.serverUrl || '').replace(/\/+$/, '');
    if (!base) {
      console.error('[TRACKING] Server URL not set. In Theme Customize → Server-Side Tracking block set "Server URL" to your API (e.g. http://localhost:3000).');
      return;
    }
    if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && base.indexOf('http://') === 0) {
      console.warn('[TRACKING] Mixed content: this page is HTTPS but Server URL is HTTP. Browsers may block the request. For local dev use a tunnel (e.g. ngrok) or run the store on HTTP.');
    }
    var url = base + '/' + endpointPath;
    const payload = JSON.stringify(eventData);

    console.log('[TRACKING] Preparing to send event:', eventData.event_name);
    console.log('[TRACKING] Target URL:', url);
    console.log('[TRACKING] Payload size:', payload.length, 'bytes');
    Logger.debug('Full payload:', eventData);

    var isCrossOrigin = false;
    try {
      var pageOrigin = window.location.origin;
      var apiOrigin = url.split('/').slice(0, 3).join('/');
      isCrossOrigin = (apiOrigin !== pageOrigin);
    } catch (e) {
      isCrossOrigin = true;
    }

    // Use fetch with credentials: 'omit' for cross-origin so backend can use Access-Control-Allow-Origin: *
    // sendBeacon can trigger CORS credentials error when backend uses wildcard
    var useFetch = isCrossOrigin || retryCount > 0 || !navigator.sendBeacon;
    if (!useFetch && navigator.sendBeacon) {
      try {
        var blob = new Blob([payload], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) {
          console.log('[TRACKING] Event sent via sendBeacon');
          return;
        }
      } catch (e) {
        console.warn('[TRACKING] sendBeacon failed:', e);
      }
    }

    // Fallback to fetch (credentials: 'omit' so backend can use Access-Control-Allow-Origin: *)
    console.log('[TRACKING] Sending event via fetch API');
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: payload,
      keepalive: true,
      credentials: 'omit'
    })
    .then(response => {
      console.log('[TRACKING] Fetch response received:', response.status, response.statusText);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      console.log('[TRACKING] Event sent successfully via fetch');
      return response.json();
    })
    .then(data => {
      if (data) {
        console.log('[TRACKING] Server response:', data);
      }
    })
    .catch(error => {
      console.error('[TRACKING] Error sending event:', error);
      
      // Retry logic
      if (retryCount < CONFIG.maxRetries) {
        const delay = CONFIG.retryDelay * Math.pow(2, retryCount); // Exponential backoff
        console.log(`[TRACKING] Retrying in ${delay}ms (attempt ${retryCount + 1}/${CONFIG.maxRetries})`);
        setTimeout(() => {
          sendEvent(eventData, retryCount + 1);
        }, delay);
      } else {
        console.error('[TRACKING] Max retries reached, event failed permanently');
      }
    });
  }

  /**
   * Build event payload for POST {BASE_URL}/api/track
   * Required: event_name. Optional: timestamp, session_id, user_id, fbp, fbc, attribution, landing_page, user_agent, product_id, order_id, email, value, etc.
   * Omits undefined/null values for a clean JSON body.
   * @param {string} eventName - API event name (page_view, product_view, add_to_cart, checkout_started, checkout_completed)
   * @param {Object} eventData - Additional event-specific data
   * @returns {Object} Payload for /api/track
   */
  function buildEventPayload(eventName, eventData = {}) {
    console.log('[TRACKING] buildEventPayload called for event:', eventName);
    console.log('[TRACKING] Additional event data:', eventData);

    const base = getTrackingParams();
    const timestamp = new Date().toISOString();
    const first_click_source = base.first_click_source || null;
    const last_click_source = base.last_click_source || null;
    const first_click_campaign = base.first_click_campaign || null;
    const last_click_campaign = base.last_click_campaign || null;

    const payload = {
      event_name: eventName,
      timestamp: timestamp,
      session_id: base.session_id || undefined,
      user_id: base.user_id || undefined,
      fbp: base.fbp || undefined,
      fbc: base.fbc || undefined,
      attribution: (first_click_source || last_click_source || first_click_campaign || last_click_campaign)
        ? { first_click_source, first_click_campaign, last_click_source, last_click_campaign }
        : undefined,
      landing_page: base.landing_page || undefined,
      user_agent: base.user_agent || undefined,
      page_url: window.location.href,
      page_title: document.title,
      ...eventData
    };

    // Remove undefined so JSON body only includes set fields
    const cleaned = {};
    Object.keys(payload).forEach(function (k) {
      if (payload[k] !== undefined && payload[k] !== null) {
        cleaned[k] = payload[k];
      }
    });

    console.log('[TRACKING] Event payload built:', { event_name: cleaned.event_name, timestamp: cleaned.timestamp });
    Logger.debug('Complete payload:', cleaned);

    return cleaned;
  }

  // ============================================================================
  // SHOPIFY WEB PIXEL API EVENT HANDLERS
  // ============================================================================

  /**
   * Handle PageView event → API: page_view
   */
  function handlePageView() {
    console.log('[TRACKING] ===== PageView event triggered =====');
    const payload = buildEventPayload('page_view');
    sendEvent(payload);
    console.log('[TRACKING] ===== PageView event handling complete =====');
  }

  /**
   * Handle ViewContent event (product viewed) → API: product_view
   */
  function handleViewContent(data) {
    console.log('[TRACKING] ===== ViewContent event triggered =====');
    console.log('[TRACKING] Shopify event data:', data);

    const productData = window.__PRODUCT__ || {};
    console.log('[TRACKING] Product data from window.__PRODUCT__:', productData);

    const productId = productData.id || data?.productId || null;
    const payload = buildEventPayload('product_view', {
      product_id: productId != null ? String(productId) : undefined,
      variant_id: productData.variantId || data?.variantId || undefined,
      product_handle: productData.handle || undefined,
      product_title: productData.title || undefined,
      product_price: productData.price || undefined,
      currency: productData.currency || 'USD'
    });

    console.log('[TRACKING] Product view payload:', { product_id: payload.product_id });
    sendEvent(payload);
    console.log('[TRACKING] ===== ViewContent event handling complete =====');
  }

  /**
   * Handle AddToCart event → API: add_to_cart
   * Accepts camelCase (productId, variantId, title) or snake_case (product_id, variant_id, product_title) for Layer 2/3.
   */
  function handleAddToCart(data) {
    console.log('[TRACKING] ===== AddToCart event triggered =====');
    console.log('[TRACKING] Event data:', data);

    var productId = data?.productId != null ? data.productId : (data?.product_id != null ? data.product_id : null);
    var variantId = data?.variantId != null ? data.variantId : (data?.variant_id != null ? data.variant_id : null);
    var title = data?.title != null ? data.title : (data?.product_title != null ? data.product_title : null);
    var price = data?.price != null ? data.price : undefined;
    var currency = data?.currency || 'USD';
    var quantity = data?.quantity != null ? parseInt(data.quantity, 10) : 1;

    const payload = buildEventPayload('add_to_cart', {
      product_id: productId != null ? String(productId) : undefined,
      variant_id: variantId != null ? String(variantId) : undefined,
      quantity: quantity,
      price: price,
      currency: currency,
      product_title: title
    });

    console.log('[TRACKING] Add to cart payload:', { product_id: payload.product_id, quantity: payload.quantity });
    sendEvent(payload);
    console.log('[TRACKING] ===== AddToCart event handling complete =====');
  }

  /**
   * Handle InitiateCheckout event → API: checkout_started
   * Accepts totalPrice, currency, lineItems or itemCount for Layer 2/3.
   */
  function handleInitiateCheckout(data) {
    console.log('[TRACKING] ===== InitiateCheckout event triggered =====');
    console.log('[TRACKING] Event data:', data);

    var numItems = data?.lineItems?.length != null ? data.lineItems.length : (data?.itemCount != null ? data.itemCount : 0);
    const payload = buildEventPayload('checkout_started', {
      value: data?.totalPrice != null ? parseFloat(String(data.totalPrice)) : undefined,
      currency: data?.currency || 'USD',
      num_items: numItems
    });

    console.log('[TRACKING] Checkout started payload:', { value: payload.value, num_items: payload.num_items });
    sendEvent(payload);
    console.log('[TRACKING] ===== InitiateCheckout event handling complete =====');
  }

  /**
   * Handle Purchase event → API: checkout_completed (order_id, email, value, ...)
   */
  function handlePurchase(data) {
    console.log('[TRACKING] ===== Purchase event triggered =====');
    console.log('[TRACKING] Shopify event data:', data);

    const customer = data?.checkout?.customer || {};
    const shippingAddress = data?.checkout?.shippingAddress || {};
    const orderValue = data?.checkout?.totalPrice?.amount != null
      ? parseFloat(String(data.checkout.totalPrice.amount))
      : undefined;

    const payload = buildEventPayload('checkout_completed', {
      order_id: data?.checkout?.order?.id || undefined,
      order_number: data?.checkout?.order?.orderNumber || undefined,
      value: orderValue,
      currency: data?.checkout?.currencyCode || 'USD',
      num_items: data?.checkout?.lineItems?.length ?? data?.checkout?.line_items?.length ?? undefined,
      email: customer?.email || undefined,
      phone: customer?.phone || undefined,
      first_name: customer?.firstName || shippingAddress?.firstName || undefined,
      last_name: customer?.lastName || shippingAddress?.lastName || undefined,
      city: shippingAddress?.city || undefined,
      state: shippingAddress?.provinceCode || undefined,
      zip: shippingAddress?.zip || undefined,
      country: shippingAddress?.countryCode || undefined,
      payment_method: (function () {
        const gateway = (data?.checkout?.paymentGatewayNames && data.checkout.paymentGatewayNames[0]) || '';
        if (gateway.toLowerCase().includes('cod') || gateway.toLowerCase().includes('cash')) return 'COD';
        if (gateway.toLowerCase().includes('partial')) return 'partial';
        return gateway ? 'prepaid' : undefined;
      })(),
      content_ids: (function () {
        var items = data?.checkout?.lineItems || data?.checkout?.line_items || [];
        if (!items.length) return undefined;
        return items.map(function (item) {
          var id = item.product && item.product.id ? item.product.id : item.product_id;
          return id != null ? String(id) : null;
        }).filter(Boolean);
      })()
    });

    console.log('[TRACKING] Purchase payload:', { order_id: payload.order_id, value: payload.value, has_email: !!payload.email });
    sendEvent(payload);
    console.log('[TRACKING] ===== Purchase event handling complete =====');
  }

  // ============================================================================
  // SHOPIFY WEB PIXEL API INTEGRATION
  // ============================================================================

  /**
   * Subscribe to Shopify Web Pixel API events (when available).
   * In Theme App Extensions only analytics.publish() is available; subscribe() exists in Web Pixel extensions.
   * If subscribe is not available, we only track page_view on init; product/cart/checkout need a Web Pixel or manual triggers.
   */
  function subscribeToEvents() {
    console.log('[TRACKING] subscribeToEvents called');
    console.log('[TRACKING] Checking for Shopify.analytics API...');

    if (typeof window.Shopify?.analytics === 'undefined') {
      console.warn('[TRACKING] Shopify Web Pixel API not available');
      return;
    }

    var analytics = window.Shopify.analytics;
    if (typeof analytics.subscribe !== 'function') {
      console.warn('[TRACKING] Shopify.analytics.subscribe is not available (Theme App Extensions only have publish). Page views are tracked on load; product/cart/checkout events require a Web Pixel extension.');
      return;
    }

    console.log('[TRACKING] Shopify Web Pixel API found, subscribing to events...');
    try {
      analytics.subscribe('page_viewed', handlePageView);
      analytics.subscribe('product_viewed', handleViewContent);
      analytics.subscribe('product_added_to_cart', handleAddToCart);
      analytics.subscribe('checkout_started', handleInitiateCheckout);
      analytics.subscribe('checkout_completed', handlePurchase);
      console.log('[TRACKING] Subscribed to all Shopify Web Pixel API events');
    } catch (e) {
      console.error('[TRACKING] Error subscribing to events:', e);
    }
  }

  // ============================================================================
  // LAYER 2: DOM-BASED ADD TO CART TRACKING (FALLBACK)
  // ============================================================================

  /**
   * Extract product data from the current page (works with most themes)
   */
  function extractProductDataFromPage() {
    Logger.debug('Extracting product data from page...');
    var productData = { product_id: null, product_title: null, price: null, currency: 'USD' };

    if (window.__PRODUCT__) {
      productData.product_id = window.__PRODUCT__.id;
      productData.product_title = window.__PRODUCT__.title;
      productData.price = window.__PRODUCT__.price;
      productData.currency = window.__PRODUCT__.currency || 'USD';
      return productData;
    }

    try {
      var jsonLd = document.querySelector('script[type="application/ld+json"]');
      if (jsonLd) {
        var schema = JSON.parse(jsonLd.textContent);
        if (schema['@type'] === 'Product') {
          productData.product_title = schema.name;
          productData.price = (schema.offers && schema.offers.price) || (schema.offers && schema.offers[0] && schema.offers[0].price);
          productData.currency = (schema.offers && schema.offers.priceCurrency) || (schema.offers && schema.offers[0] && schema.offers[0].priceCurrency) || 'USD';
        }
      }
    } catch (e) { /* ignore */ }

    try {
      var productIdMeta = document.querySelector('meta[property="product:product_id"]');
      var priceMeta = document.querySelector('meta[property="product:price:amount"]');
      var currencyMeta = document.querySelector('meta[property="product:price:currency"]');
      var titleMeta = document.querySelector('meta[property="og:title"]');
      if (productIdMeta) productData.product_id = productIdMeta.getAttribute('content');
      if (priceMeta) productData.price = parseFloat(priceMeta.getAttribute('content'));
      if (currencyMeta) productData.currency = currencyMeta.getAttribute('content') || 'USD';
      if (titleMeta) productData.product_title = titleMeta.getAttribute('content');
    } catch (e) { /* ignore */ }

    if (!productData.product_title) {
      var titleParts = document.title.split('–');
      productData.product_title = (titleParts[0] && titleParts[0].trim()) || document.title;
    }
    if (!productData.price) {
      var priceEl = document.querySelector('.price .money, .product-price .money, [data-product-price], .product__price');
      if (priceEl) {
        var priceText = priceEl.textContent.replace(/[^0-9.]/g, '');
        productData.price = parseFloat(priceText);
      }
    }
    return productData;
  }

  /**
   * Universal Add to Cart detection via DOM (Layer 2)
   */
  function initDOMCartTracking() {
    console.log('[TRACKING] Initializing DOM-based cart tracking (Layer 2)...');

    document.addEventListener('submit', function (e) {
      var form = e.target;
      var action = (form.action || form.getAttribute('action') || '').toString();
      var isCartForm = action.indexOf('/cart/add') !== -1 ||
        form.id === 'product-form' || form.id === 'add-to-cart-form' ||
        (form.className && form.className.indexOf && form.className.indexOf('product-form') !== -1) ||
        form.querySelector('[name="id"]');
      if (!isCartForm) return;
      console.log('[TRACKING] Add to cart form submission detected');
      try {
        var formData = new FormData(form);
        var variantId = formData.get('id');
        var quantity = parseInt(formData.get('quantity'), 10) || 1;
        if (variantId) {
          var productData = extractProductDataFromPage();
          setTimeout(function () {
            handleAddToCart({
              variantId: variantId,
              productId: productData.product_id,
              product_title: productData.product_title,
              price: productData.price,
              currency: productData.currency,
              quantity: quantity
            });
          }, 100);
        }
      } catch (err) { Logger.error('DOM form tracking:', err); }
    }, true);

    document.addEventListener('click', function (e) {
      var button = e.target.closest ? e.target.closest('button, [role="button"], a') : null;
      if (!button) return;
      var name = (button.name || '').toLowerCase();
      var type = (button.type || '').toLowerCase();
      var cls = (button.className || '').toLowerCase();
      var text = (button.textContent || '').toLowerCase();
      var dataAction = (button.getAttribute && button.getAttribute('data-action')) || '';
      var isAddBtn = name === 'add' || type === 'submit' ||
        cls.indexOf('add-to-cart') !== -1 || cls.indexOf('btn-add-to-cart') !== -1 ||
        cls.indexOf('product-form__submit') !== -1 || dataAction === 'add-to-cart' ||
        text.indexOf('add to cart') !== -1 || text.indexOf('add to bag') !== -1;
      if (!isAddBtn) return;
      console.log('[TRACKING] Add to cart button click detected');
      var form = button.closest ? button.closest('form') : null;
      if (!form) form = document.querySelector('form[action*="/cart/add"]') || document.querySelector('#product-form');
      if (form) {
        try {
          var formData = new FormData(form);
          var variantId = formData.get('id') || (button.getAttribute && button.getAttribute('data-variant-id'));
          var quantity = parseInt(formData.get('quantity'), 10) || 1;
          if (variantId) {
            var productData = extractProductDataFromPage();
            setTimeout(function () {
              handleAddToCart({
                variantId: variantId,
                productId: productData.product_id,
                product_title: productData.product_title,
                price: productData.price,
                currency: productData.currency,
                quantity: quantity
              });
            }, 200);
          }
        } catch (err) { Logger.error('DOM click tracking:', err); }
      }
    }, true);
    console.log('[TRACKING] DOM-based cart tracking initialized');
  }

  // ============================================================================
  // LAYER 3: AJAX INTERCEPTOR FOR CART OPERATIONS
  // ============================================================================

  /**
   * Get current cart from Shopify /cart.js
   */
  function getCartData() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cartData) {
        Logger.debug('Cart data fetched:', cartData);
        return cartData;
      })
      .catch(function (e) {
        Logger.error('Error fetching cart:', e);
        return null;
      });
  }

  /**
   * Intercept fetch and XHR for /cart/add and checkout (Layer 3)
   */
  function interceptCartAPI() {
    console.log('[TRACKING] Initializing AJAX cart interceptor (Layer 3)...');

    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url);
      var options = arguments[1] || {};
      if (url && (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/add.js') !== -1)) {
        try {
          var body = options.body;
          var data = null;
          if (typeof body === 'string') {
            try { data = JSON.parse(body); } catch (e) { data = Object.fromEntries(new URLSearchParams(body)); }
          } else if (body && typeof FormData !== 'undefined' && body instanceof FormData) {
            data = Object.fromEntries(body);
          }
          if (data && (data.id || data.variant_id)) {
            console.log('[TRACKING] Fetch cart/add detected');
            setTimeout(function () {
              handleAddToCart({
                variant_id: data.id || data.variant_id,
                quantity: parseInt(data.quantity, 10) || 1,
                product_id: data.product_id,
                product_title: data.product_title,
                price: data.price,
                currency: data.currency || 'USD'
              });
            }, 100);
          }
        } catch (e) { Logger.error('Fetch intercept:', e); }
      }
      if (url && (url.indexOf('/checkout') !== -1 || url.indexOf('/cart/checkout') !== -1)) {
        getCartData().then(function (cartData) {
          if (cartData && cartData.item_count) {
            handleInitiateCheckout({
              totalPrice: cartData.total_price / 100,
              currency: cartData.currency,
              lineItems: cartData.items,
              itemCount: cartData.item_count
            });
          }
        });
      }
      return originalFetch.apply(this, arguments);
    };

    var originalXHROpen = XMLHttpRequest.prototype.open;
    var originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._trackingUrl = url;
      this._trackingMethod = method;
      return originalXHROpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      var url = this._trackingUrl;
      if (url && (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/add.js') !== -1)) {
        try {
          var data = null;
          if (typeof body === 'string') {
            try { data = JSON.parse(body); } catch (e) { data = Object.fromEntries(new URLSearchParams(body)); }
          }
          if (data && (data.id || data.variant_id)) {
            console.log('[TRACKING] XHR cart/add detected');
            setTimeout(function () {
              handleAddToCart({
                variant_id: data.id || data.variant_id,
                quantity: parseInt(data.quantity, 10) || 1,
                product_id: data.product_id,
                product_title: data.product_title,
                price: data.price,
                currency: data.currency || 'USD'
              });
            }, 100);
          }
        } catch (e) { Logger.error('XHR intercept:', e); }
      }
      return originalXHRSend.apply(this, arguments);
    };
    console.log('[TRACKING] AJAX cart interceptor initialized');
  }

  // ============================================================================
  // CHECKOUT INITIATION TRACKING
  // ============================================================================

  function initCheckoutTracking() {
    console.log('[TRACKING] Initializing checkout tracking...');

    document.addEventListener('click', function (e) {
      var button = e.target.closest ? e.target.closest('button, a, [role="button"]') : null;
      if (!button) return;
      var href = (button.href || '').toString();
      var text = (button.textContent || '').toLowerCase();
      var cls = (button.className || '').toLowerCase();
      var name = (button.name || '').toLowerCase();
      var dataAction = (button.getAttribute && button.getAttribute('data-action')) || '';
      var isCheckout = name === 'checkout' || href.indexOf('/checkout') !== -1 || href.indexOf('/cart/checkout') !== -1 ||
        cls.indexOf('checkout') !== -1 || cls.indexOf('cart__checkout') !== -1 ||
        dataAction === 'checkout' || text.indexOf('checkout') !== -1 || text.indexOf('proceed to checkout') !== -1;
      if (!isCheckout) return;
      console.log('[TRACKING] Checkout button click detected');
      getCartData().then(function (cartData) {
        if (cartData) {
          handleInitiateCheckout({
            totalPrice: cartData.total_price / 100,
            currency: cartData.currency,
            lineItems: cartData.items,
            itemCount: cartData.item_count
          });
        }
      });
    }, true);

    if (window.location.pathname.indexOf('/checkout') !== -1) {
      getCartData().then(function (cartData) {
        if (cartData && cartData.item_count > 0) {
          handleInitiateCheckout({
            totalPrice: cartData.total_price / 100,
            currency: cartData.currency,
            lineItems: cartData.items,
            itemCount: cartData.item_count
          });
        }
      });
    }
    console.log('[TRACKING] Checkout tracking initialized');
  }

  // ============================================================================
  // PURCHASE TRACKING (THANK YOU PAGE)
  // ============================================================================

  function initPurchaseTracking() {
    console.log('[TRACKING] Checking for purchase completion...');
    var path = window.location.pathname || '';
    var hasThankYou = path.indexOf('/thank_you') !== -1 || path.indexOf('/orders/') !== -1 ||
      document.querySelector('.os-order-number');
    if (!hasThankYou) return;

    console.log('[TRACKING] Thank you / order page detected');
    if (window.Shopify && window.Shopify.checkout) {
      var checkout = window.Shopify.checkout;
      var eventData = {
        checkout: {
          order: { id: checkout.order_id, orderNumber: checkout.order_number },
          totalPrice: { amount: checkout.total_price },
          currencyCode: checkout.currency,
          lineItems: checkout.line_items || [],
          customer: {
            email: checkout.email,
            firstName: (checkout.billing_address && checkout.billing_address.first_name) || (checkout.shipping_address && checkout.shipping_address.first_name),
            lastName: (checkout.billing_address && checkout.billing_address.last_name) || (checkout.shipping_address && checkout.shipping_address.last_name)
          },
          shippingAddress: checkout.shipping_address
        }
      };
      console.log('[TRACKING] Purchase data from Shopify.checkout');
      handlePurchase(eventData);
      return;
    }
    try {
      var orderNumberEl = document.querySelector('.os-order-number');
      var orderNumber = orderNumberEl && orderNumberEl.textContent ? orderNumberEl.textContent.trim() : null;
      if (orderNumber) {
        var totalEl = document.querySelector('.payment-due__price');
        var totalText = totalEl && totalEl.textContent ? totalEl.textContent.replace(/[^0-9.]/g, '') : '';
        var eventData = {
          checkout: {
            order: { orderNumber: orderNumber },
            totalPrice: { amount: totalText ? parseFloat(totalText) : 0 },
            currencyCode: 'USD',
            lineItems: []
          }
        };
        handlePurchase(eventData);
      }
    } catch (e) { Logger.error('Purchase DOM parse:', e); }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize tracking system (Layer 1 + Layer 2 + Layer 3 + checkout + purchase)
   */
  function init() {
    console.log('[TRACKING] ========================================');
    console.log('[TRACKING] INITIALIZING TRACKING SYSTEM');
    console.log('[TRACKING] ========================================');
    
    if (!CONFIG.enableTracking) {
      console.log('[TRACKING] Tracking is disabled in configuration');
      return;
    }

    console.log('[TRACKING] Step 1: Initializing attribution tracking...');
    initFirstClick();
    updateLastClick();

    console.log('[TRACKING] Step 2: Setting landing page...');
    getLandingPage();

    console.log('[TRACKING] Step 3: Subscribing to Shopify Web Pixel API (Layer 1)...');
    subscribeToEvents();

    console.log('[TRACKING] Step 4: Initializing DOM-based cart tracking (Layer 2)...');
    initDOMCartTracking();

    console.log('[TRACKING] Step 5: Initializing AJAX interceptors (Layer 3)...');
    interceptCartAPI();

    console.log('[TRACKING] Step 6: Initializing checkout tracking...');
    initCheckoutTracking();

    console.log('[TRACKING] Step 7: Checking for purchase completion...');
    initPurchaseTracking();

    console.log('[TRACKING] Step 8: Tracking initial page view...');
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        handlePageView();
      });
    } else {
      handlePageView();
    }

    console.log('[TRACKING] ========================================');
    console.log('[TRACKING] TRACKING SYSTEM INITIALIZED (Layer 1 + 2 + 3)');
    console.log('[TRACKING] ========================================');
  }

  // ============================================================================
  // START INITIALIZATION
  // ============================================================================

  console.log('[TRACKING] Script execution started');
  console.log('[TRACKING] Document ready state:', document.readyState);
  console.log('[TRACKING] window.Shopify available:', typeof window.Shopify !== 'undefined');
  console.log('[TRACKING] window.__TRACKING_CONFIG__ available:', typeof window.__TRACKING_CONFIG__ !== 'undefined');

  // Wait for DOM and Shopify API to be ready
  if (document.readyState === 'loading') {
    console.log('[TRACKING] Document still loading, waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', function() {
      console.log('[TRACKING] DOMContentLoaded fired, waiting 100ms for Shopify API...');
      // Give Shopify time to load
      setTimeout(function() {
        console.log('[TRACKING] Initialization timeout fired');
        init();
      }, 100);
    });
  } else {
    console.log('[TRACKING] Document already loaded, waiting 100ms for Shopify API...');
    setTimeout(function() {
      console.log('[TRACKING] Initialization timeout fired');
      init();
    }, 100);
  }

  // Also listen for Shopify API availability
  if (typeof window.Shopify === 'undefined') {
    console.log('[TRACKING] Shopify API not available yet, starting polling...');
    let pollCount = 0;
    const checkShopify = setInterval(function() {
      pollCount++;
      console.log(`[TRACKING] Polling for Shopify API (attempt ${pollCount})...`);
      if (typeof window.Shopify !== 'undefined') {
        console.log('[TRACKING] Shopify API detected!');
        clearInterval(checkShopify);
        init();
      }
    }, 100);
    
    // Stop checking after 10 seconds
    setTimeout(function() {
      clearInterval(checkShopify);
      if (typeof window.Shopify === 'undefined') {
        console.warn('[TRACKING] Shopify API not detected after 10 seconds, some events may not work');
        console.warn('[TRACKING] Proceeding with initialization anyway...');
        init();
      }
    }, 10000);
  } else {
    console.log('[TRACKING] Shopify API already available');
  }

  console.log('[TRACKING] Script execution complete, initialization scheduled');

})();
